import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { Suspense, lazy } from 'react'
import { useAuthStore } from './store/auth'
import { authApi } from './api/auth'

// Layouts
import { AppLayout } from './components/layout/AppLayout'
import { AdminLayout } from './components/layout/AdminLayout'
import { SettingsLayout } from './pages/Settings'

// Pages
import { LoginPage } from './pages/Login'
import { ForgotPasswordPage } from './pages/ForgotPassword'
import { ResetPasswordPage } from './pages/ResetPassword'
import { AcceptInvitePage } from './pages/AcceptInvite'
import { CompaniesPage as AdminCompaniesPage } from './pages/admin/CompaniesPage'
import { UsersPage as AdminUsersPage } from './pages/admin/UsersPage'
import { StatsPage as AdminStatsPage } from './pages/admin/StatsPage'
import { LogsPage as AdminLogsPage } from './pages/admin/LogsPage'
import { ProfilePage as AdminProfilePage } from './pages/admin/ProfilePage'
import { LeadsListPage } from './pages/LeadsList'
import { LeadDetailPage } from './pages/LeadDetail'
import { DealsListPage } from './pages/DealsList'
import { DealDetailPage } from './pages/DealDetail'
import { ContactsListPage } from './pages/ContactsList'
import { OrganizationsListPage } from './pages/OrganizationsList'
import { RecordingsPage } from './pages/RecordingsPage'
import { CalendarPage } from './pages/Calendar'
import { AppointmentsPage } from './pages/Appointments'
import { MailPage } from './pages/mail/MailPage'
import { WhatsAppPage } from './pages/whatsapp/WhatsAppPage'
import { GeneralSettings } from './pages/settings/GeneralSettings'
import { UsersSettings } from './pages/settings/UsersSettings'
import { CustomStatusesSettings } from './pages/settings/CustomStatusesSettings'
import { CustomFieldsSettings, AssignmentRulesSettings, SlaPoliciesSettings, SavedViewsSettings } from './pages/settings/OtherSettings'
import { EmailAccountsSettings, EmailTemplatesSettings } from './pages/settings/EmailSettings'
import { LeadSourcesSettings } from './pages/settings/LeadSourcesSettings'
import { DocumentRecycleBin } from './pages/settings/DocumentRecycleBin'
import { WhatsappSettings } from './pages/settings/WhatsappSettings'
import { ServicesSettings } from './pages/settings/ServicesSettings'
import { InvoicingSettings } from './pages/settings/InvoicingSettings'
import { AppointmentsSyncSettings } from './pages/settings/AppointmentsSyncSettings'
import { InvoicesList } from './pages/invoices/InvoicesList'
import { InvoiceBuilder } from './pages/invoices/InvoiceBuilder'
import { PageSpinner } from './components/ui/Spinner'
import { CallOverlay } from './components/CallOverlay'
import { initSip } from './lib/sip'
import { callConfigsApi } from './api/callConfigs'
import { useState } from 'react'

const DashboardPage = lazy(async () => {
  const module = await import('./pages/Dashboard')
  return { default: module.DashboardPage }
})

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'administrator') return <Navigate to="/" replace />
  return <>{children}</>
}

function RequireOwner({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  if (user.role === 'sales_user') return <Navigate to="leads" replace />
  return <>{children}</>
}

function RootRedirect() {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  if (user.role === 'administrator') return <Navigate to="/admin" replace />
  if (user.companyId) return <Navigate to={`/company/${user.companyId}/leads`} replace />
  return <Navigate to="/login" replace />
}

export default function App() {
  const { setAuth, user } = useAuthStore()
  const [loading, setLoading] = useState(true)

  // On mount: restore session + pre-register SIP (handles page refresh)
  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (token) {
      authApi.me()
        .then(r => {
          setAuth(r.data, token)
          if (r.data.companyId) {
            callConfigsApi.getMyConfig(r.data.companyId)
              .then(res => {
                const cfg = res.data
                if (cfg.hasConfig) initSip(cfg)
              })
              .catch(() => {}) // silently skip if call config unavailable
          }
        })
        .catch(() => {
          localStorage.removeItem('access_token')
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  // After fresh login: user changes in zustand but mount effect already ran — init SIP here
  useEffect(() => {
    if (!user?.companyId) return
    callConfigsApi.getMyConfig(user.companyId)
      .then(res => {
        const cfg = res.data
        if (cfg.hasConfig) initSip(cfg)
      })
      .catch(() => {})
  }, [user?.id])

  if (loading) return <PageSpinner />

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />

        <Route path="/admin" element={
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        }>
          <Route index element={<Navigate to="companies" replace />} />
          <Route path="companies" element={<AdminCompaniesPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="stats" element={<AdminStatsPage />} />
          <Route path="logs" element={<AdminLogsPage />} />
          <Route path="recordings" element={<RecordingsPage />} />
          <Route path="profile" element={<AdminProfilePage />} />
        </Route>

        <Route path="/company/:companyId" element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }>
          <Route index element={<Navigate to="leads" replace />} />
          <Route
            path="dashboard"
            element={
              <Suspense fallback={<PageSpinner />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route path="leads" element={<LeadsListPage />} />
          <Route path="leads/:leadId" element={<LeadDetailPage />} />
          <Route path="deals" element={<DealsListPage />} />
          <Route path="deals/:dealId" element={<DealDetailPage />} />
          <Route path="contacts" element={<ContactsListPage />} />
          <Route path="contacts/:contactId" element={<ContactsListPage />} />
          <Route path="organizations" element={<RequireOwner><OrganizationsListPage /></RequireOwner>} />
          <Route path="organizations/:orgId" element={<RequireOwner><OrganizationsListPage /></RequireOwner>} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="appointments" element={<AppointmentsPage />} />
          <Route path="mail" element={<MailPage />} />
          <Route path="whatsapp" element={<WhatsAppPage />} />
          <Route path="recordings" element={<RequireOwner><RecordingsPage /></RequireOwner>} />
          <Route path="invoices" element={<InvoicesList />} />
          <Route path="invoices/new" element={<InvoiceBuilder />} />
          <Route path="invoices/:invoiceId/edit" element={<InvoiceBuilder />} />

          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="general" replace />} />
            <Route path="general" element={<GeneralSettings />} />
            <Route path="users" element={<UsersSettings />} />
            <Route path="email-accounts" element={<RequireAdmin><EmailAccountsSettings /></RequireAdmin>} />
            <Route path="email-templates" element={<RequireAdmin><EmailTemplatesSettings /></RequireAdmin>} />
            <Route path="whatsapp" element={<RequireOwner><WhatsappSettings /></RequireOwner>} />
            <Route path="lead-sources" element={<RequireAdmin><LeadSourcesSettings /></RequireAdmin>} />
            <Route path="assignment-rules" element={<RequireAdmin><AssignmentRulesSettings /></RequireAdmin>} />
            <Route path="sla" element={<RequireAdmin><SlaPoliciesSettings /></RequireAdmin>} />
            <Route path="custom-fields" element={<CustomFieldsSettings />} />
            <Route path="custom-statuses" element={<CustomStatusesSettings />} />
            <Route path="views" element={<RequireAdmin><SavedViewsSettings /></RequireAdmin>} />
            <Route path="document-recycle-bin" element={<RequireOwner><DocumentRecycleBin /></RequireOwner>} />
            <Route path="services" element={<RequireOwner><ServicesSettings /></RequireOwner>} />
            <Route path="invoicing" element={<RequireOwner><InvoicingSettings /></RequireOwner>} />
            <Route path="appointments-sync" element={<RequireAdmin><AppointmentsSyncSettings /></RequireAdmin>} />
          </Route>
        </Route>

        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global call overlay — persists across navigation */}
      <audio id="pbx-audio-out" autoPlay />
      <CallOverlay />
    </BrowserRouter>
  )
}
